import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Avatar,
  Snackbar,
  Alert,
  Skeleton,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack,
  Business,
  EventNote,
  Add,
  Edit,
  Delete,
  ReceiptLong,
  ShoppingCart,
  Payments as PaymentsIcon,
  Inventory2,
  WorkspacePremium,
  History,
  Groups,
  Event as EventIcon,
  Star,
  AccountBalanceWallet,
  ErrorOutline,
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from 'recharts';

import crmService, { CrmCustomer360, Contact, CreateContactInput } from '../../services/crmService';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { useControlRoomColors, ControlRoomColors } from '../../components/common/controlRoom';
import { formatIndianNumber, formatCurrency } from '../../utils/currency';
import CrmAiAssist from '../../components/crm/CrmAiAssist';
import CrmAttachmentsPanel from '../../components/crm/CrmAttachmentsPanel';

// ---------------------------------------------------------------------------
// CRM CUSTOMER 360 — the flagship account view. Merges the CRM overlay
// (health, buying committee, pipeline, lead origin) with the AR/order
// position already surfaced by the finance Customer 360. Read-only except for
// the Contacts tab (buying committee CRUD) and the Meeting Brief, which is
// assembled entirely from data already loaded on this page — no invented
// figures, no AI claim.
//
// The API mixes camelCase (opportunities, profile enrichment) with snake_case
// (leadOrigin, base order/invoice rows) depending on which layer produced the
// field, so every read goes through `pick()` rather than assuming one casing.
// ---------------------------------------------------------------------------

const TAB_LABELS = ['Overview', 'Timeline', 'Contacts', 'Opportunities', 'Orders', 'Invoices', 'Payments', 'Products'];

/** Reads the first present key — the 360 payload mixes camelCase and snake_case per source table. */
const pick = (obj: any, ...keys: string[]): any => {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
};

const asNum = (v: any): number | null => {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const money = (v: any): React.ReactNode => {
  const n = asNum(v);
  return n === null ? null : formatIndianNumber(n);
};

const fmtDate = (v: any): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const HEALTH_TONE: Record<string, StatusTone> = {
  Healthy: 'success',
  Watch: 'info',
  'At Risk': 'warning',
  Critical: 'error',
};

const IMPACT_TONE: Record<string, StatusTone> = { positive: 'success', negative: 'error', neutral: 'neutral' };

const STAGE_TINTS: Record<string, string> = {
  NEW: '#64748b',
  QUALIFICATION: '#0891b2',
  PROPOSAL: '#7c3aed',
  NEGOTIATION: '#d97706',
  WON: '#16a34a',
  LOST: '#dc2626',
};

const ROLE_FLAGS: { key: keyof Contact; label: string }[] = [
  { key: 'is_decision_maker', label: 'Decision Maker' },
  { key: 'is_influencer', label: 'Influencer' },
  { key: 'is_technical', label: 'Technical' },
  { key: 'is_finance', label: 'Finance' },
  { key: 'is_procurement', label: 'Procurement' },
];

const isTruthy = (v: any): boolean => v === 1 || v === true || v === '1';

const timelineMeta = (kind: string, c: ControlRoomColors): { icon: React.ReactNode; color: string } => {
  switch (kind) {
    case 'invoice':
      return { icon: <ReceiptLong fontSize="small" />, color: c.accent };
    case 'order':
      return { icon: <ShoppingCart fontSize="small" />, color: c.cyan };
    case 'payment':
      return { icon: <PaymentsIcon fontSize="small" />, color: c.good };
    case 'activity':
      return { icon: <EventIcon fontSize="small" />, color: c.purple };
    default:
      return { icon: <History fontSize="small" />, color: c.sub };
  }
};

// ---------------------------------------------------------------------------
// Contact form dialog — shared by Add and Edit.
// ---------------------------------------------------------------------------

interface ContactFormState {
  name: string;
  designation: string;
  department: string;
  email: string;
  phone: string;
  mobile: string;
  isPrimary: boolean;
  isDecisionMaker: boolean;
  isInfluencer: boolean;
  isTechnical: boolean;
  isFinance: boolean;
  isProcurement: boolean;
  relationshipStrength: string;
  preferredChannel: string;
  notes: string;
}

const emptyContactForm: ContactFormState = {
  name: '',
  designation: '',
  department: '',
  email: '',
  phone: '',
  mobile: '',
  isPrimary: false,
  isDecisionMaker: false,
  isInfluencer: false,
  isTechnical: false,
  isFinance: false,
  isProcurement: false,
  relationshipStrength: 'NEUTRAL',
  preferredChannel: '',
  notes: '',
};

const contactToForm = (c: Contact): ContactFormState => ({
  name: c.name || '',
  designation: c.designation || '',
  department: c.department || '',
  email: c.email || '',
  phone: c.phone || '',
  mobile: c.mobile || '',
  isPrimary: isTruthy(c.is_primary),
  isDecisionMaker: isTruthy(c.is_decision_maker),
  isInfluencer: isTruthy(c.is_influencer),
  isTechnical: isTruthy(c.is_technical),
  isFinance: isTruthy(c.is_finance),
  isProcurement: isTruthy(c.is_procurement),
  relationshipStrength: c.relationship_strength || 'NEUTRAL',
  preferredChannel: c.preferred_channel || '',
  notes: c.notes || '',
});

const ContactFormDialog: React.FC<{
  open: boolean;
  initial: Contact | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (input: CreateContactInput) => void;
}> = ({ open, initial, saving, onClose, onSubmit }) => {
  const [form, setForm] = useState<ContactFormState>(emptyContactForm);
  const [nameError, setNameError] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? contactToForm(initial) : emptyContactForm);
      setNameError(false);
    }
  }, [open, initial]);

  const set = <K extends keyof ContactFormState>(key: K, value: ContactFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setNameError(true);
      return;
    }
    const input: CreateContactInput = {
      name: form.name.trim(),
      designation: form.designation.trim() || null,
      department: form.department.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      mobile: form.mobile.trim() || null,
      isPrimary: form.isPrimary,
      isDecisionMaker: form.isDecisionMaker,
      isInfluencer: form.isInfluencer,
      isTechnical: form.isTechnical,
      isFinance: form.isFinance,
      isProcurement: form.isProcurement,
      relationshipStrength: form.relationshipStrength || undefined,
      preferredChannel: form.preferredChannel || undefined,
      notes: form.notes.trim() || undefined,
    };
    onSubmit(input);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{initial ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Name"
              required
              fullWidth
              size="small"
              value={form.name}
              error={nameError}
              helperText={nameError ? 'Name is required' : ' '}
              onChange={(e) => { set('name', e.target.value); if (e.target.value.trim()) setNameError(false); }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Designation" fullWidth size="small" value={form.designation} onChange={(e) => set('designation', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Department" fullWidth size="small" value={form.department} onChange={(e) => set('department', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Email" type="email" fullWidth size="small" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Phone" fullWidth size="small" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Mobile" fullWidth size="small" value={form.mobile} onChange={(e) => set('mobile', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="rel-strength-label">Relationship Strength</InputLabel>
              <Select
                labelId="rel-strength-label"
                label="Relationship Strength"
                value={form.relationshipStrength}
                onChange={(e) => set('relationshipStrength', e.target.value)}
              >
                <MenuItem value="STRONG">Strong</MenuItem>
                <MenuItem value="NEUTRAL">Neutral</MenuItem>
                <MenuItem value="WEAK">Weak</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="pref-channel-label">Preferred Channel</InputLabel>
              <Select
                labelId="pref-channel-label"
                label="Preferred Channel"
                value={form.preferredChannel}
                onChange={(e) => set('preferredChannel', e.target.value)}
              >
                <MenuItem value=""><em>Not set</em></MenuItem>
                <MenuItem value="EMAIL">Email</MenuItem>
                <MenuItem value="PHONE">Phone</MenuItem>
                <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                <MenuItem value="IN_PERSON">In Person</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Role in buying committee</Typography>
            <Stack direction="row" flexWrap="wrap" sx={{ mt: 0.25 }}>
              <FormControlLabel control={<Checkbox size="small" checked={form.isPrimary} onChange={(e) => set('isPrimary', e.target.checked)} />} label="Primary" />
              <FormControlLabel control={<Checkbox size="small" checked={form.isDecisionMaker} onChange={(e) => set('isDecisionMaker', e.target.checked)} />} label="Decision Maker" />
              <FormControlLabel control={<Checkbox size="small" checked={form.isInfluencer} onChange={(e) => set('isInfluencer', e.target.checked)} />} label="Influencer" />
              <FormControlLabel control={<Checkbox size="small" checked={form.isTechnical} onChange={(e) => set('isTechnical', e.target.checked)} />} label="Technical" />
              <FormControlLabel control={<Checkbox size="small" checked={form.isFinance} onChange={(e) => set('isFinance', e.target.checked)} />} label="Finance" />
              <FormControlLabel control={<Checkbox size="small" checked={form.isProcurement} onChange={(e) => set('isProcurement', e.target.checked)} />} label="Procurement" />
            </Stack>
          </Grid>
          <Grid size={12}>
            <TextField label="Notes" fullWidth size="small" multiline minRows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const CrmCustomer360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const customerId = Number(id);
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [data, setData] = useState<CrmCustomer360 | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [savingContact, setSavingContact] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [meetingBriefOpen, setMeetingBriefOpen] = useState(false);

  const load = useCallback(async () => {
    if (!customerId || Number.isNaN(customerId)) return;
    setLoading(true);
    setError(null);
    try {
      const d = await crmService.getCustomer360(customerId);
      setData(d);
      setContacts(Array.isArray(d.contacts) ? d.contacts : []);
    } catch (e: any) {
      setError(e.message || 'Failed to load customer 360');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const refreshContacts = useCallback(async () => {
    if (!customerId || Number.isNaN(customerId)) return;
    try {
      const r = await crmService.getContacts(customerId);
      setContacts(Array.isArray(r.contacts) ? r.contacts : []);
    } catch {
      // keep the previously loaded list rather than blanking it on a transient failure
    }
  }, [customerId]);

  const openAddContact = () => { setEditingContact(null); setContactDialogOpen(true); };
  const openEditContact = (ct2: Contact) => { setEditingContact(ct2); setContactDialogOpen(true); };

  const handleSaveContact = async (input: CreateContactInput) => {
    setSavingContact(true);
    try {
      if (editingContact) {
        await crmService.updateContact(editingContact.id, input);
        setToast({ msg: 'Contact updated', severity: 'success' });
      } else {
        await crmService.createContact(customerId, input);
        setToast({ msg: 'Contact added', severity: 'success' });
      }
      setContactDialogOpen(false);
      setEditingContact(null);
      await refreshContacts();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to save contact', severity: 'error' });
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!deleteTarget) return;
    try {
      await crmService.deleteContact(deleteTarget.id);
      setToast({ msg: 'Contact removed', severity: 'success' });
      setDeleteTarget(null);
      await refreshContacts();
    } catch (e: any) {
      setToast({ msg: e.message || 'Failed to remove contact', severity: 'error' });
    }
  };

  // ---- Derived, defensively-guarded views of the payload ----
  const profile = data?.profile || {};
  const summary = data?.summary || {};
  const health = data?.health || { score: 0, band: 'Watch', signals: [] };
  const openOpps = Array.isArray(data?.opportunities?.open) ? data!.opportunities.open : [];
  const weightedOpenTotal = data?.opportunities?.weightedOpenTotal;
  const leadOrigin = data?.leadOrigin || null;
  const productsPurchased = Array.isArray(data?.productsPurchased) ? data!.productsPurchased : [];
  const timeline = Array.isArray(data?.timeline) ? data!.timeline : [];
  const orders = Array.isArray(data?.orders) ? data!.orders : [];
  const invoices = Array.isArray(data?.invoices) ? data!.invoices : [];
  const payments = useMemo(() => timeline.filter((t) => t.kind === 'payment'), [timeline]);

  const companyName = pick(profile, 'name', 'company_name', 'companyName') || 'Unnamed Account';
  const industry = pick(profile, 'industry');
  const segment = pick(profile, 'segment');
  const territory = pick(profile, 'territory');
  const lifecycleStage = pick(profile, 'lifecycleStage', 'lifecycle_stage');
  const accountOwnerName = pick(profile, 'accountOwnerName', 'account_owner_name');
  const creditLimit = asNum(pick(profile, 'creditLimit', 'credit_limit'));
  const gstin = pick(profile, 'gstin');
  const city = pick(profile, 'city');
  const state = pick(profile, 'state');

  const lifetimeRevenue = pick(summary, 'billed', 'totalRevenue', 'total_revenue');
  const outstanding = pick(summary, 'outstanding');
  const overdue = pick(summary, 'overdue');

  const topProductsChart = useMemo(
    () =>
      [...productsPurchased]
        .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
        .slice(0, 6)
        .map((p) => ({
          name: p.productName && p.productName.length > 16 ? `${p.productName.slice(0, 15)}…` : p.productName || '—',
          fullName: p.productName || '—',
          revenue: p.revenue || 0,
        })),
    [productsPurchased]
  );

  const decisionContacts = useMemo(
    () => contacts.filter((ct2) => isTruthy(ct2.is_decision_maker) || isTruthy(ct2.is_primary)),
    [contacts]
  );
  const recentActivity = useMemo(() => timeline.slice(0, 6), [timeline]);
  const concerns = useMemo(() => (health.signals || []).filter((s) => s.impact === 'negative'), [health.signals]);

  // ---- States ----

  if (loading && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={160} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={90} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 6, md: 3 }}>
              <Skeleton variant="rounded" height={92} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={44} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 1 }}>Back</Button>
        <TableSurface>
          <ErrorState message={error || 'Customer not found'} onRetry={load} />
        </TableSurface>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 1 }}>Back</Button>

      <PageHeader
        title={companyName}
        subtitle={[industry, segment, territory, city && state ? `${city}, ${state}` : city || state].filter(Boolean).join('  ·  ')}
        actions={
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            {segment && <TypePill label={String(segment).toUpperCase()} tint={c.purple} />}
            {lifecycleStage && <TypePill label={String(lifecycleStage).toUpperCase()} tint={c.cyan} />}
            <Button variant="contained" startIcon={<EventNote />} onClick={() => setMeetingBriefOpen(true)}>
              Meeting Brief
            </Button>
          </Stack>
        }
      />

      {/* Identity strip: owner / territory / GSTIN / credit limit */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Account Owner</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{accountOwnerName || '—'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Territory</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{territory || '—'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">GSTIN</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{gstin || '—'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Credit Limit</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{creditLimit === null ? '—' : formatIndianNumber(creditLimit)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Health widget */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, sm: 3 }}>
            <Stack alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={0.5}>
              <Typography variant="overline" color="text.secondary">Account Health</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{health.score}</Typography>
              <StatusDot label={health.band} tone={HEALTH_TONE[health.band] || 'neutral'} />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 9 }}>
            <Stack spacing={1}>
              {(health.signals || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No health signals computed yet.</Typography>
              )}
              {(health.signals || []).map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ mt: 0.6 }}>
                    <StatusDot label="" tone={IMPACT_TONE[s.impact] || 'neutral'} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                    {s.detail && <Typography variant="caption" color="text.secondary">{s.detail}</Typography>}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* KPI row */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Lifetime Revenue" value={money(lifetimeRevenue)} icon={<AccountBalanceWallet />} color="#2563eb" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Outstanding" value={money(outstanding)} icon={<ReceiptLong />} color="#7c3aed" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Overdue" value={money(overdue)} icon={<ErrorOutline />} color="#dc2626" />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard title="Open Pipeline (Weighted)" value={money(weightedOpenTotal)} icon={<WorkspacePremium />} color="#059669" />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, minHeight: 40, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        {TAB_LABELS.map((t) => <Tab key={t} label={t} />)}
      </Tabs>

      {/* ---------------- Overview ---------------- */}
      {tab === 0 && (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Revenue Trend</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Month-by-month revenue is not provided by this view.
              </Typography>
              <TableEmptyState title="No monthly revenue trend" hint="This account's revenue history is available as Orders, Invoices and Payments in the tabs above." />
            </Paper>

            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Top Products by Revenue</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Lifetime invoiced revenue by product, top 6
              </Typography>
              {topProductsChart.length ? (
                <Box sx={{ height: 260, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topProductsChart} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
                      <CartesianGrid {...gridProps(ct)} />
                      <XAxis dataKey="name" {...axisProps(ct)} />
                      <YAxis {...axisProps(ct)} width={62} tickFormatter={(v) => formatIndianNumber(v)} />
                      <RTooltip
                        {...tooltipStyle(ct)}
                        formatter={(v: any) => [formatCurrency(Number(v)), 'Revenue']}
                        labelFormatter={(_l, payload) => (payload && payload[0] ? (payload[0].payload as any).fullName : '')}
                      />
                      <Bar dataKey="revenue" name="Revenue" fill={ct.series(0)} radius={[3, 3, 0, 0]} maxBarSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <TableEmptyState title="No product purchases yet" hint="Revenue by product will appear once this account has invoiced sales." />
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>All Health Signals</Typography>
              <Stack spacing={1.25}>
                {(health.signals || []).map((s, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ mt: 0.6 }}><StatusDot label="" tone={IMPACT_TONE[s.impact] || 'neutral'} /></Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                      {s.detail && <Typography variant="caption" color="text.secondary">{s.detail}</Typography>}
                    </Box>
                  </Box>
                ))}
                {(health.signals || []).length === 0 && <Typography variant="body2" color="text.secondary">No signals available.</Typography>}
              </Stack>
            </Paper>

            {leadOrigin && (
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Lead Origin</Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Source</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{pick(leadOrigin, 'lead_source', 'leadSource') || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{pick(leadOrigin, 'status') || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Created</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtDate(pick(leadOrigin, 'created_at', 'createdAt'))}</Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {/* ---------------- Timeline ---------------- */}
      {tab === 1 && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Activity Timeline</Typography>
          {timeline.length === 0 ? (
            <TableEmptyState title="No activity yet" hint="Orders, invoices, payments and logged activities will appear here as they happen." />
          ) : (
            <Box sx={{ position: 'relative', pl: 4 }}>
              <Box sx={{ position: 'absolute', left: 15, top: 6, bottom: 6, width: 2, bgcolor: 'divider' }} />
              <Stack spacing={2.5}>
                {timeline.map((t, i) => {
                  const meta = timelineMeta(t.kind, c);
                  const clickable = !!t.link;
                  return (
                    <Box key={i} sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -32,
                          top: 0,
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: `${meta.color}1a`,
                          color: meta.color,
                        }}
                      >
                        {meta.icon}
                      </Box>
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          cursor: clickable ? 'pointer' : 'default',
                          '&:hover': clickable ? { bgcolor: 'action.hover' } : {},
                        }}
                        onClick={() => clickable && navigate(t.link as string)}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{fmtDate(t.ts)}</Typography>
                          </Box>
                          {t.amount != null && (
                            <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                              {formatIndianNumber(Number(t.amount))}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Paper>
      )}

      {/* ---------------- Contacts ---------------- */}
      {tab === 2 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Buying Committee</Typography>
            <Button variant="contained" size="small" startIcon={<Add />} onClick={openAddContact}>Add Contact</Button>
          </Stack>
          {contacts.length === 0 ? (
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <TableEmptyState title="No contacts yet" hint="Add the buying committee for this account." />
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {contacts.map((ctc) => (
                <Grid key={ctc.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                          <Avatar sx={{ bgcolor: c.accent, width: 36, height: 36, fontSize: '0.9rem' }}>
                            {(ctc.name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ctc.name}
                              </Typography>
                              {isTruthy(ctc.is_primary) && <Star sx={{ fontSize: 15, color: '#eda100' }} />}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {[ctc.designation, ctc.department].filter(Boolean).join(' · ') || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                        <Stack direction="row">
                          <IconButton size="small" onClick={() => openEditContact(ctc)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => setDeleteTarget(ctc)}><Delete fontSize="small" /></IconButton>
                        </Stack>
                      </Stack>

                      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1.5 }}>
                        {ROLE_FLAGS.filter((r) => isTruthy(ctc[r.key])).map((r) => (
                          <Chip key={r.key} size="small" label={r.label} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: `${c.accent}14`, color: c.accent }} />
                        ))}
                        {ROLE_FLAGS.every((r) => !isTruthy(ctc[r.key])) && (
                          <Typography variant="caption" color="text.disabled">No role tagged</Typography>
                        )}
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">{ctc.email || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{[ctc.phone, ctc.mobile].filter(Boolean).join(' / ') || '—'}</Typography>
                        {ctc.relationship_strength && (
                          <Typography variant="caption" color="text.secondary">Relationship: {ctc.relationship_strength}</Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ---------------- Opportunities ---------------- */}
      {tab === 3 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Opportunity</TableCell>
                  <TableCell sx={headerCellSx} align="right">Value</TableCell>
                  <TableCell sx={headerCellSx} align="right">Probability</TableCell>
                  <TableCell sx={headerCellSx} align="right">Weighted</TableCell>
                  <TableCell sx={headerCellSx}>Stage</TableCell>
                  <TableCell sx={headerCellSx}>Expected Close</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {openOpps.length === 0 ? (
                  <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No open opportunities" hint="This account has no active pipeline right now." /></TableCell></TableRow>
                ) : (
                  openOpps.map((o) => {
                    const name = pick(o, 'opportunityName', 'opportunity_name') || 'Untitled';
                    const value = asNum(pick(o, 'estimatedValue', 'estimated_value')) || 0;
                    const probability = asNum(pick(o, 'probability')) || 0;
                    const stage = pick(o, 'stage') || '—';
                    const closeDate = pick(o, 'expectedClosureDate', 'expected_closure_date');
                    const weighted = (value * probability) / 100;
                    return (
                      <TableRow key={o.id} hover sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => navigate(`/crm/opportunity/${o.id}/workspace`)}>
                        <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(value)}</TableCell>
                        <TableCell align="right">{probability}%</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatIndianNumber(weighted)}</TableCell>
                        <TableCell><TypePill label={String(stage)} tint={STAGE_TINTS[stage] || c.accent} /></TableCell>
                        <TableCell>{fmtDate(closeDate)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Orders ---------------- */}
      {tab === 4 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Order</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow><TableCell colSpan={4} sx={{ border: 0 }}><TableEmptyState title="No orders yet" /></TableCell></TableRow>
                ) : (
                  orders.map((o: any, i: number) => (
                    <TableRow key={i} hover sx={bodyRowSx}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{pick(o, 'ref', 'order_number') || '—'}</TableCell>
                      <TableCell>{fmtDate(pick(o, 'date', 'order_date'))}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(pick(o, 'amount', 'total_amount'))}</TableCell>
                      <TableCell><StatusDot label={pick(o, 'status') || '—'} tone="neutral" /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Invoices ---------------- */}
      {tab === 5 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Invoice</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                  <TableCell sx={headerCellSx} align="right">Paid</TableCell>
                  <TableCell sx={headerCellSx}>Due Date</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={6} sx={{ border: 0 }}><TableEmptyState title="No invoices yet" /></TableCell></TableRow>
                ) : (
                  invoices.map((inv: any, i: number) => (
                    <TableRow key={i} hover sx={bodyRowSx}>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{pick(inv, 'ref', 'invoice_number') || '—'}</TableCell>
                      <TableCell>{fmtDate(pick(inv, 'date', 'invoice_date'))}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(pick(inv, 'amount', 'total_amount'))}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(pick(inv, 'paid', 'paid_amount'))}</TableCell>
                      <TableCell>{fmtDate(pick(inv, 'dueDate', 'due_date'))}</TableCell>
                      <TableCell><StatusDot label={pick(inv, 'status') || '—'} tone="neutral" /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Payments ---------------- */}
      {tab === 6 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell sx={headerCellSx}>Detail</TableCell>
                  <TableCell sx={headerCellSx} align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow><TableCell colSpan={3} sx={{ border: 0 }}><TableEmptyState title="No payments recorded" hint="Payments appear here once receipts are posted against invoices." /></TableCell></TableRow>
                ) : (
                  payments.map((p, i) => (
                    <TableRow key={i} hover sx={{ ...bodyRowSx, cursor: p.link ? 'pointer' : 'default' }} onClick={() => p.link && navigate(p.link)}>
                      <TableCell>{fmtDate(p.ts)}</TableCell>
                      <TableCell>{p.label}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{p.amount != null ? formatIndianNumber(Number(p.amount)) : '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Products ---------------- */}
      {tab === 7 && (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Product</TableCell>
                  <TableCell sx={headerCellSx}>Code</TableCell>
                  <TableCell sx={headerCellSx} align="right">Qty</TableCell>
                  <TableCell sx={headerCellSx} align="right">Revenue</TableCell>
                  <TableCell sx={headerCellSx} align="right">Invoices</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productsPurchased.length === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ border: 0 }}><TableEmptyState title="No products purchased yet" /></TableCell></TableRow>
                ) : (
                  productsPurchased.map((p, i) => (
                    <TableRow key={p.productId ?? i} hover sx={bodyRowSx}>
                      <TableCell sx={{ fontWeight: 600 }}>{p.productName || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.productCode || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatIndianNumber(p.qty || 0)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatIndianNumber(p.revenue || 0)}</TableCell>
                      <TableCell align="right">{p.invoiceCount || 0}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---------------- Contact dialog ---------------- */}
      <ContactFormDialog
        open={contactDialogOpen}
        initial={editingContact}
        saving={savingContact}
        onClose={() => { setContactDialogOpen(false); setEditingContact(null); }}
        onSubmit={handleSaveContact}
      />

      {/* ---------------- Delete contact confirm ---------------- */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove Contact</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <b>{deleteTarget?.name}</b> from this account&apos;s buying committee?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteContact}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Meeting Brief ---------------- */}
      <Dialog open={meetingBriefOpen} onClose={() => setMeetingBriefOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Meeting Brief — {companyName}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
            Assembled from real system data — orders, invoices, pipeline and CRM activity on record.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Financial Position</Typography>
              <Grid container spacing={1.5}>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary">Lifetime Revenue</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{money(lifetimeRevenue) ?? '—'}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{money(outstanding) ?? '—'}</Typography>
                </Grid>
                <Grid size={4}>
                  <Typography variant="caption" color="text.secondary">Overdue</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: overdue ? 'error.main' : 'inherit' }}>{money(overdue) ?? '—'}</Typography>
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Open Pipeline {weightedOpenTotal != null && `(weighted ${formatIndianNumber(Number(weightedOpenTotal))})`}
              </Typography>
              {openOpps.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No open opportunities.</Typography>
              ) : (
                <Stack spacing={0.75}>
                  {openOpps.map((o) => (
                    <Box key={o.id} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{pick(o, 'opportunityName', 'opportunity_name')} <Typography component="span" variant="caption" color="text.secondary">({pick(o, 'stage')})</Typography></Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(asNum(pick(o, 'estimatedValue', 'estimated_value')) || 0)}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Key Contacts</Typography>
              {decisionContacts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No decision-maker or primary contact on record.</Typography>
              ) : (
                <Stack spacing={0.75}>
                  {decisionContacts.map((ctc) => (
                    <Box key={ctc.id}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{ctc.name} {ctc.designation ? `— ${ctc.designation}` : ''}</Typography>
                      <Typography variant="caption" color="text.secondary">{[ctc.email, ctc.phone || ctc.mobile].filter(Boolean).join(' · ') || '—'}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recent Activity</Typography>
              {recentActivity.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No recent activity on record.</Typography>
              ) : (
                <Stack spacing={0.5}>
                  {recentActivity.map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{t.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{fmtDate(t.ts)}</Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {concerns.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'error.main' }}>Health Concerns</Typography>
                <Stack spacing={0.5}>
                  {concerns.map((s, i) => (
                    <Typography key={i} variant="body2">• {s.label}{s.detail ? ` — ${s.detail}` : ''}</Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMeetingBriefOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {!!customerId && !Number.isNaN(customerId) && (
        <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <CrmAiAssist mode="customer" id={customerId} />
          <CrmAttachmentsPanel entityType="customer" entityId={customerId} />
        </Box>
      )}

      <Snackbar open={!!toast} autoHideDuration={3200} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default CrmCustomer360Page;
